"use client";

/**
 * MatchBreakdown
 *
 * The keyword and competency tiles with a Details panel, shared by the fit page (matched against the profile) and the
 * resume page (matched against the generated resume), so both show the same lists the same way. The panel opens as
 * the lower half of the card and overlays the page below it, so nothing shifts. Closes on an outside click or Escape.
 * Keywords are the job's terms; competencies are the job's requirements. Neither list is capped. Must-haves (called
 * knockouts in code) are the job's pass/fail conditions (location, work authorization, salary...), checked against the
 * candidate's profile and autofill data; they are a gate (met, not met, not provided), never a percentage.
 */

import { useEffect, useRef, useState, type ReactNode } from "react";
import { ChevronDown, HelpCircle } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { summarizeKnockouts } from "@/lib/utils/fit/knockouts";
import { statusFromScore } from "@/lib/utils/fit/profileMatch";
import type { KnockoutMatch, ProfileRequirementMatch, ProfileTermMatch } from "@/types";

export type BreakdownTerm = Pick<ProfileTermMatch, "term" | "support">;
export type BreakdownCompetency = Pick<ProfileRequirementMatch, "requirementId" | "requirement" | "level">;
export type BreakdownKnockout = Pick<KnockoutMatch, "requirementId" | "requirement" | "hard" | "status">;

interface MatchBreakdownProps {
  /** What the job is matched against; words the labels ("in your profile", "Your resume"). */
  subject: "profile" | "resume";
  terms: BreakdownTerm[];
  /** Scored requirements only (logistics excluded). */
  competencies: BreakdownCompetency[];
  /** The job's knockout requirements. Omit or leave empty when the job has none. */
  knockouts?: BreakdownKnockout[];
  /** Show a "May not qualify" line under the tiles when a required knockout conflicts. The fit page shows it in its header instead. */
  screenedNote?: boolean;
  /** "card" sits under a header inside a larger card; "compact" is a small standalone strip. */
  variant?: "card" | "compact";
  /** Shown above the tiles, inside the same card. */
  header?: ReactNode;
  /** Shown below the tiles, inside the same card. */
  footer?: ReactNode;
  /** Shown at the bottom of the details panel, for example when the match was scored. */
  detailsNote?: string;
  /** Keeps the details open as part of the page, below the tiles and pushing the content below it down, with no Details button. */
  expanded?: boolean;
  /**
   * Show the keyword and competency tiles as percentages (the weighted scores the overall score is made of) instead of
   * counts. Leave unset for counts. A null value falls back to the count for that tile.
   */
  scores?: { keywords: number | null; competencies: number | null };
  /** Bigger tiles. */
  large?: boolean;
  /** A "?" next to a tile's label that explains it. */
  help?: { keywords?: string; competencies?: string };
  testId?: string;
}

// Gaps first: not shown, then partly shown, then shown. Array.sort is stable, so the job's own order holds within a group.
const LEVEL_ORDER: Record<ProfileRequirementMatch["level"], number> = { none: 0, partial: 1, shown: 2 };

const LEVEL_LABEL: Record<ProfileRequirementMatch["level"], string> = {
  shown: "Shown",
  partial: "Partly shown",
  none: "Not shown",
};

const LEVEL_TEXT: Record<ProfileRequirementMatch["level"], string> = {
  shown: "text-success font-bold",
  partial: "text-muted-foreground font-bold",
  none: "text-destructive font-bold",
};

const VARIANT = {
  card: {
    wrap: "relative",
    closed: "rounded-3xl bg-card p-4 shadow-card",
    open: "rounded-t-3xl bg-card p-4 shadow-card",
    panel: "rounded-b-3xl bg-card",
    row: "mt-3",
  },
  compact: {
    wrap: "relative w-xl max-w-full",
    closed: "rounded-2xl bg-surface px-4 py-3",
    open: "rounded-t-2xl bg-surface px-4 py-3",
    panel: "rounded-b-2xl bg-surface",
    row: "",
  },
} as const;

/** Colors for a percentage score, with the same cutoffs as the overall score so a tile never contradicts the headline. */
function toneForScore(score: number) {
  const status = statusFromScore(score);
  if (status === "green") return { text: "text-score-high", bar: "bg-score-high" };
  if (status === "yellow") return { text: "text-score-medium", bar: "bg-score-medium" };
  return { text: "text-score-low", bar: "bg-score-low" };
}

function tone(matched: number, total: number) {
  const pct = total ? (matched / total) * 100 : 0;
  if (pct >= 80) return { text: "text-score-high", bar: "bg-score-high" };
  if (pct >= 50) return { text: "text-score-medium", bar: "bg-score-medium" };
  return { text: "text-score-low", bar: "bg-score-low" };
}

const STATUS_LABEL: Record<KnockoutMatch["status"], string> = {
  clear: "Met",
  conflict: "Not met",
  unknown: "Not provided",
};

function HelpTip({ label, text }: { label: string; text: string }) {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={`About ${label}`}
          onPointerEnter={(e) => e.pointerType !== "touch" && setOpen(true)}
          onPointerLeave={() => setOpen(false)}
          className="inline-flex text-muted-foreground transition-colors hover:text-surface-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-full"
        >
          <HelpCircle className="h-3.5 w-3.5" />
        </button>
      </PopoverTrigger>
      <PopoverContent side="top" className="w-64 p-3 text-xs leading-relaxed" onOpenAutoFocus={(e) => e.preventDefault()}>
        {text}
      </PopoverContent>
    </Popover>
  );
}

function Stat({
  label,
  matched,
  total,
  toneOverride,
  percent,
  large = false,
  help,
}: {
  label: string;
  matched: number;
  total: number;
  toneOverride?: ReturnType<typeof tone>;
  /** When set, the tile shows this percentage (and bar) instead of matched/total. */
  percent?: number | null;
  large?: boolean;
  help?: string;
}) {
  const showPercent = typeof percent === "number";
  const t = toneOverride ?? (showPercent ? toneForScore(percent) : tone(matched, total));
  const fill = showPercent ? percent : total ? (matched / total) * 100 : 0;
  return (
    <div>
      <p className={`flex items-center gap-1 text-muted-foreground ${large ? "text-sm" : "text-xs"}`}>
        {label}
        {help && <HelpTip label={label} text={help} />}
      </p>
      <p className={`font-semibold leading-tight tabular-nums ${large ? "text-3xl" : "text-lg"} ${t.text}`}>
        {showPercent ? `${Math.round(percent)}%` : `${matched}/${total}`}
      </p>
      <div className={`mt-1 overflow-hidden rounded-full bg-muted ${large ? "h-1.5 w-32" : "h-1 w-20"}`}>
        <div className={`h-full rounded-full ${t.bar}`} style={{ width: `${fill}%` }} />
      </div>
    </div>
  );
}

function Chips({ label, items, muted }: { label: string; items: string[]; muted?: boolean }) {
  if (items.length === 0) return null;
  return (
    <div>
      <p className="mb-1 text-muted-foreground">{label}</p>
      <div className="flex flex-wrap gap-1.5">
        {items.map((k) => (
          <span
            key={k}
            className={`rounded-full px-2 py-0.5 ${
              muted ? "border border-border text-muted-foreground" : "bg-muted text-surface-foreground"
            }`}
          >
            {k}
          </span>
        ))}
      </div>
    </div>
  );
}

export function MatchBreakdown({
  subject,
  terms,
  competencies,
  knockouts = [],
  screenedNote = true,
  variant = "card",
  header,
  footer,
  detailsNote,
  expanded = false,
  scores,
  large = false,
  help,
  testId,
}: MatchBreakdownProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const hasLists = terms.length > 0 || competencies.length > 0 || knockouts.length > 0;
  if (!hasLists && !header) return null;

  const isOpen = expanded || open;
  const v = VARIANT[variant];
  const keywordsIn = terms.filter((t) => t.support !== "none").map((t) => t.term);
  const keywordsOut = terms.filter((t) => t.support === "none").map((t) => t.term);
  const shown = competencies.filter((r) => r.level === "shown").length;
  const noun = subject === "resume" ? "resume" : "profile";
  const ko = summarizeKnockouts(knockouts);
  const koTone =
    ko.conflict > 0
      ? { text: "text-score-low", bar: "bg-score-low" }
      : ko.unknown > 0
        ? { text: "text-score-medium", bar: "bg-score-medium" }
        : { text: "text-score-high", bar: "bg-score-high" };

  return (
    <div ref={ref} className={v.wrap} data-testid={testId}>
      <div className={`border border-border ${isOpen ? `${v.open} border-b-transparent` : v.closed}`}>
        {header}
        {hasLists && (
          <div className={`flex items-center ${large ? "gap-10" : "gap-6"} ${v.row}`}>
            {terms.length > 0 && (
              <Stat
                label="Keywords"
                matched={keywordsIn.length}
                total={terms.length}
                percent={scores?.keywords}
                large={large}
                help={help?.keywords}
              />
            )}
            {competencies.length > 0 && (
              <Stat
                label="Competencies"
                matched={shown}
                total={competencies.length}
                percent={scores?.competencies}
                large={large}
                help={help?.competencies}
              />
            )}
            {knockouts.length > 0 && <Stat label="Must-haves" matched={ko.clear} total={ko.total} toneOverride={koTone} />}
            {!expanded && (
              <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                aria-expanded={open}
                className="ml-auto inline-flex items-center gap-1 rounded-full border border-border px-3 py-1 text-xs text-muted-foreground transition-colors hover:text-surface-foreground"
              >
                Details
                <ChevronDown className={`h-3 w-3 transition-transform ${open ? "rotate-180" : ""}`} />
              </button>
            )}
          </div>
        )}
        {screenedNote && ko.screenedOut && (
          <p className="mt-2 text-xs font-medium text-score-low">May not qualify: a required must-have is not met</p>
        )}
        {footer}
      </div>

      {isOpen && (
        <div
          className={`border border-t-0 border-border px-4 pb-4 text-xs ${v.panel} ${
            expanded ? "" : "absolute inset-x-0 top-full z-20 max-h-[60vh] overflow-y-auto shadow-card"
          }`}
        >
          <div className="space-y-4 border-t border-border pt-3">
            {knockouts.length > 0 && (
              <div>
                <div className="grid grid-cols-[minmax(0,2fr)_minmax(0,1fr)] gap-x-4 px-2 pb-1 text-muted-foreground">
                  <p>Must-haves in the job description</p>
                  <p>Your profile</p>
                </div>
                <div className="overflow-hidden rounded-lg">
                  {knockouts.map((k) => (
                    <div
                      key={k.requirementId}
                      className="grid grid-cols-[minmax(0,2fr)_minmax(0,1fr)] gap-x-4 px-2 py-1.5 odd:bg-muted/60"
                    >
                      <p className="text-surface-foreground">
                        {k.requirement}
                        {k.hard && <span className="ml-1.5 text-muted-foreground">Required</span>}
                      </p>
                      <p
                        className={
                          k.status === "conflict"
                            ? "font-medium text-score-low"
                            : k.status === "unknown"
                              ? "text-muted-foreground"
                              : "text-surface-foreground"
                        }
                      >
                        {STATUS_LABEL[k.status]}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {terms.length > 0 && (
              <div className="space-y-2">
                <Chips label={`Keywords not in your ${noun}`} items={keywordsOut} muted />
                <Chips label={`Keywords in your ${noun}`} items={keywordsIn} />
              </div>
            )}
            {competencies.length > 0 && (
              <div>
                <div className="grid grid-cols-[minmax(0,2fr)_minmax(0,1fr)] gap-x-4 px-2 pb-1 text-muted-foreground">
                  <p>Competencies in the job description</p>
                  <p>{subject === "resume" ? "Your resume" : "Your profile"}</p>
                </div>
                <div className="overflow-hidden rounded-lg">
                  {[...competencies].sort((a, b) => LEVEL_ORDER[a.level] - LEVEL_ORDER[b.level]).map((r) => (
                    <div
                      key={r.requirementId}
                      className="grid grid-cols-[minmax(0,2fr)_minmax(0,1fr)] gap-x-4 px-2 py-1.5 odd:bg-muted/60"
                    >
                      <p className="text-surface-foreground">{r.requirement}</p>
                      <p className={LEVEL_TEXT[r.level]}>
                        {LEVEL_LABEL[r.level]}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {detailsNote && <p className="text-muted-foreground">{detailsNote}</p>}
          </div>
        </div>
      )}
    </div>
  );
}
